import { Platform } from 'react-native';
import * as Localization from 'expo-localization';
import * as Linking from 'expo-linking';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import type { AuthUser } from '@/context/AuthContext';
import { posthog } from '@/providers/PostHogProvider';
import { shouldCaptureAttribution } from '@/utils/attributionDedupe';
import { APP_BUILD_PROPERTIES } from '@/services/buildInfo';
import { sanitizeEventProperties } from '@/services/analyticsPrivacy';

const UTM_STORAGE_KEY = 'mc_utm_params';
const ANON_ID_STORAGE_KEY = 'mc_ph_anon_id';
const ALIAS_APPLIED_PREFIX = 'mc_ph_alias_applied';
const INSTALL_ATTRIBUTION_KEY = 'mc_app_install_attribution';
const BILLING_SNAPSHOT_KEY = 'mc_billing_snapshot';
const ACTIVATED_KEY = 'mc_user_activated';
const ANALYSIS_COMPLETION_COUNT_KEY = 'mc_analysis_completion_count';
const PAYWALL_FIRST_VIEW_KEY = 'mc_paywall_first_view_at';
const PAYWALL_CHECKOUT_INITIATED_KEY = 'mc_paywall_checkout_initiated';
const PAYWALL_LEFT_WITHOUT_CHECKOUT_KEY = 'mc_paywall_left_without_checkout_at';
const COMEBACK_OFFER_SHOWN_KEY = 'mc_comeback_offer_shown';
const COMEBACK_OFFER_DISMISSED_KEY = 'mc_comeback_offer_dismissed';
const COMEBACK_OFFER_DECLINED_KEY = 'mc_comeback_offer_declined';
const FINAL_OFFER_DISMISSED_KEY = 'mc_final_offer_dismissed';
const FINAL_OFFER_DECLINED_KEY = 'mc_final_offer_declined';

export type UTMParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  landing_url?: string;
  landing_path?: string;
  referrer?: string;
  utm_captured_at?: string;
};

const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'landing_url',
  'landing_path',
  'referrer',
];

const getLocale = () => {
  const primary = Localization.getLocales()?.[0];
  const locale = primary?.languageTag || primary?.languageCode || 'en';
  return locale.split('-')[0] || 'en';
};

const baseProperties = (properties?: Record<string, any>) => ({
  platform: Platform.OS,
  locale: getLocale(),
  environment:
    process.env.EXPO_PUBLIC_POSTHOG_ENV ||
    process.env.EXPO_PUBLIC_ENVIRONMENT ||
    (process.env.NODE_ENV === 'production' ? 'production' : 'development'),
  ...APP_BUILD_PROPERTIES,
  ...properties,
});

const toStringParam = (value: string | string[] | undefined | null) => {
  if (Array.isArray(value)) return value[0];
  if (value === undefined || value === null) return undefined;
  return String(value);
};

const parseQueryString = (value: string): Record<string, string> => {
  const trimmed = value.startsWith('?') ? value.slice(1) : value;
  const params: Record<string, string> = {};
  trimmed.split('&').forEach((pair) => {
    if (!pair) return;
    const [rawKey, rawValue = ''] = pair.split('=');
    if (!rawKey) return;
    try {
      const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
      const val = decodeURIComponent(rawValue.replace(/\+/g, ' '));
      params[key] = val;
    } catch {
      params[rawKey] = rawValue;
    }
  });
  return params;
};

const extractUtmParams = (params: Record<string, string | undefined>) => {
  const keys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid',
  ];
  const utm: Record<string, string> = {};
  keys.forEach((key) => {
    const value = params[key];
    if (value) {
      utm[key] = value;
    }
  });
  return utm;
};

export async function getStoredUTMParameters(): Promise<UTMParams> {
  try {
    const raw = await AsyncStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as UTMParams;
  } catch {
    return {};
  }
}

export async function storeUTMParameters(params: UTMParams) {
  try {
    await AsyncStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // no-op
  }
}

export async function registerBaseProperties(extra?: Record<string, any>) {
  if (!posthog) return;
  const utm = await getStoredUTMParameters();
  await posthog.register({
    ...baseProperties(),
    ...utm,
    ...extra,
  });
}

export async function captureUTMFromURL(url?: string) {
  const targetUrl = url || (await Linking.getInitialURL()) || undefined;
  if (!targetUrl) return null;

  const parsed = Linking.parse(targetUrl);
  const queryParams = (parsed.queryParams || {}) as Record<string, string | string[] | undefined>;

  const attributionParams: UTMParams = {};

  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = toStringParam(queryParams[key]);
    if (value) {
      (attributionParams as Record<string, string>)[key] = value;
    }
  });

  const anonId = toStringParam(queryParams.ph_anon_id);

  // expo-linking on web re-emits the unchanged URL on every window `message`
  // event, so this runs on most re-renders. Only act when the params are new.
  if (!shouldCaptureAttribution({ ...attributionParams, ph_anon_id: anonId })) {
    return null;
  }

  if (anonId) {
    await AsyncStorage.setItem(ANON_ID_STORAGE_KEY, anonId);
  }

  if (Object.keys(attributionParams).length > 0) {
    const merged: UTMParams = {
      ...(await getStoredUTMParameters()),
      ...attributionParams,
      utm_captured_at: new Date().toISOString(),
    };
    await storeUTMParameters(merged);
    if (posthog) {
      await posthog.register(merged);
      posthog.capture('app_utm_captured', baseProperties(merged));
    }
    return merged;
  }

  return null;
}

export async function getAnonymousId() {
  return AsyncStorage.getItem(ANON_ID_STORAGE_KEY);
}

export async function getStoredAttributionForAuth(): Promise<UTMParams & { posthog_distinct_id?: string }> {
  const utm = await getStoredUTMParameters();
  const anonId = await getAnonymousId();
  return {
    ...utm,
    posthog_distinct_id: anonId || undefined,
  };
}

const captureEvent = (event: string, properties?: Record<string, any>) => {
  if (!posthog) return;
  const name = event.startsWith('app_') ? event : `app_${event}`;
  // Privacy guard: personal/content keys never leave the device (CLAUDE.md §5).
  posthog.capture(name, baseProperties(sanitizeEventProperties(properties)));
};

export const trackScreen = (name: string, properties?: Record<string, any>) => {
  captureEvent(`${name}_screen_viewed`, properties);
};

export const trackAuthEvent = (event: string, properties?: Record<string, any>) => {
  captureEvent(event, properties);
};

export type SampleReportSurface =
  | 'onboarding_slide'
  | 'dashboard_empty_state'
  | 'chat_empty_state'
  | 'library_empty_state'
  | 'activation_screen'
  | 'plan_selection'
  | 'plan_reveal';

export const trackSampleReportViewed = (
  surface: SampleReportSurface,
  properties?: Record<string, any>,
) => {
  captureEvent('sample_report_viewed', {
    surface,
    ...properties,
  });
};

export const trackSampleReportSectionViewed = (section: string) => {
  captureEvent('sample_report_section_viewed', { section });
};

export const trackFeatureMapItemTapped = (feature: string) => {
  captureEvent('feature_map_item_tapped', { feature });
};

// Guided product tour (QA F4). `tour: 'guided'` separates these from older
// `tour_step_viewed` events in funnels. Ids and enums only (no content).
type GuidedTourSource = 'auto' | 'help' | 'profile';

export const trackTourStarted = (role: string, source: GuidedTourSource, stepCount: number) => {
  captureEvent('tour_started', { tour: 'guided', role, source, step_count: stepCount });
};

export const trackTourStepViewed = (
  step: string,
  properties: { stepIndex: number; role: string; presentation: 'spotlight' | 'card' },
) => {
  captureEvent('tour_step_viewed', {
    tour: 'guided',
    step,
    step_index: properties.stepIndex,
    role: properties.role,
    presentation: properties.presentation,
  });
};

export const trackTourCompleted = (role: string, source: GuidedTourSource, stepCount: number) => {
  captureEvent('tour_completed', { tour: 'guided', role, source, step_count: stepCount });
};

export const trackTourSkipped = (step: string, stepIndex: number, role: string, source: GuidedTourSource) => {
  captureEvent('tour_skipped', { tour: 'guided', step, step_index: stepIndex, role, source });
};

export const trackTourReplayed = (source: Exclude<GuidedTourSource, 'auto'>) => {
  captureEvent('tour_replayed', { tour: 'guided', source });
};

export const trackHintOpened = (hint: string) => {
  captureEvent('hint_opened', { hint });
};

export const trackChecklistItemCompleted = (item: string, phase: 'activation' | 'level_up') => {
  captureEvent('checklist_item_completed', { item, phase });
};

export const trackChecklistCompleted = () => {
  captureEvent('checklist_completed');
};

export const trackMagicLinkSent = (intent: 'signup' | 'login', resend: boolean) => {
  captureEvent('magic_link_sent', { intent, resend });
};

export const trackMagicLinkConsumed = (isNewAccount: boolean) => {
  captureEvent('magic_link_consumed', { is_new_account: isNewAccount });
};

export type CheckoutFailureProperties = Record<string, any> & {
  error_code: string;
  error_type: string;
  surface: string;
};

export const trackCheckoutFailure = (properties: CheckoutFailureProperties) => {
  captureEvent('checkout_failed', properties);
};

export const trackCheckoutErrorShown = (properties: CheckoutFailureProperties) => {
  captureEvent('checkout_error_shown', properties);
};

export const trackFeatureUsage = (
  feature: string,
  action: string,
  properties?: Record<string, any>
) => {
  captureEvent(`${feature}_${action}`, properties);
};

/**
 * Canonical activation milestone — the single "user got value from the tool"
 * event for funnels. Fires exactly once per device (first completed analysis)
 * and stamps an `activated_at` person property so PostHog can compute
 * activation rate without OR-ing several events.
 */
export async function markActivated(properties?: Record<string, any>) {
  try {
    const already = await AsyncStorage.getItem(ACTIVATED_KEY);
    if (already) return false;
    await AsyncStorage.setItem(ACTIVATED_KEY, '1');
    if (posthog) {
      captureEvent('user_activated', {
        ...properties,
        $set_once: { activated_at: new Date().toISOString() },
      });
    }
    return true;
  } catch {
    // no-op — never let analytics block product behaviour
    return false;
  }
}

export async function recordAnalysisCompleted(): Promise<number> {
  try {
    const previous = Number(await AsyncStorage.getItem(ANALYSIS_COMPLETION_COUNT_KEY)) || 0;
    const next = previous + 1;
    await AsyncStorage.setItem(ANALYSIS_COMPLETION_COUNT_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export async function identifyUser(user: AuthUser | null) {
  if (!posthog || !user?.id) return;
  const userId = String(user.id);
  const userType = user.user_type || 'b2c';

  const utmProps = {
    utm_source: user.utm_source,
    utm_medium: user.utm_medium,
    utm_campaign: user.utm_campaign,
    utm_term: user.utm_term,
    utm_content: user.utm_content,
    utm_captured_at: user.utm_captured_at,
  } as Record<string, any>;

  posthog.identify(userId, {
    $set: {
      email: user.email,
      name: user.full_name,
      user_type: userType,
      plan_tier: user.plan_tier,
      school_id: user.school_id ?? null,
      ...utmProps,
    },
    $set_once: {
      first_seen_at: new Date().toISOString(),
    },
  });

  await registerBaseProperties({
    user_type: userType,
    plan_tier: user.plan_tier,
  });

  const anonId = await getAnonymousId();
  if (anonId) {
    const aliasKey = `${ALIAS_APPLIED_PREFIX}:${anonId}:${userId}`;
    const alreadyAliased = await AsyncStorage.getItem(aliasKey);
    if (!alreadyAliased) {
      posthog.alias(anonId);
      await AsyncStorage.setItem(aliasKey, '1');
    }
  }
}

export async function resetIdentity() {
  if (!posthog) return;
  posthog.reset();
  await registerBaseProperties();
}

export async function trackAppInstallAttribution() {
  if (!posthog || Platform.OS === 'web') return;
  try {
    const alreadyTracked = await AsyncStorage.getItem(INSTALL_ATTRIBUTION_KEY);
    if (alreadyTracked) return;

    let initialUrl: string | null = null;
    try {
      initialUrl = await Linking.getInitialURL();
    } catch {
      initialUrl = null;
    }

    const deepLinkParams = initialUrl
      ? (Linking.parse(initialUrl).queryParams as Record<string, string | string[] | undefined>)
      : {};
    const deepLinkUtm = extractUtmParams(
      Object.fromEntries(
        Object.entries(deepLinkParams || {}).map(([key, value]) => [key, toStringParam(value)])
      ) as Record<string, string | undefined>
    );

    let installReferrer: string | null = null;
    let installReferrerParams: Record<string, string> = {};
    if (Platform.OS === 'android') {
      try {
        installReferrer = await Application.getInstallReferrerAsync();
        if (installReferrer) {
          installReferrerParams = parseQueryString(installReferrer);
        }
      } catch {
        installReferrer = null;
      }
    }

    const installReferrerUtm = extractUtmParams(installReferrerParams);
    const combinedUtm = {
      ...installReferrerUtm,
      ...deepLinkUtm,
    };

    if (Object.keys(combinedUtm).length > 0) {
      const merged: UTMParams = {
        ...(await getStoredUTMParameters()),
        ...combinedUtm,
        utm_captured_at: new Date().toISOString(),
      };
      await storeUTMParameters(merged);
      await posthog.register(merged);
    }

    const installSource =
      Platform.OS === 'android'
        ? installReferrer
          ? 'google_play'
          : 'android_unknown'
        : Platform.OS === 'ios'
        ? 'app_store_or_testflight'
        : 'unknown';

    posthog.capture(
      'app_install_attributed',
      baseProperties({
        install_source: installSource,
        install_referrer: installReferrer || undefined,
        initial_url: initialUrl || undefined,
        ...combinedUtm,
      })
    );

    await AsyncStorage.setItem(INSTALL_ATTRIBUTION_KEY, new Date().toISOString());
  } catch {
    // no-op
  }
}

type BillingSnapshot = {
  status: string;
  plan_tier?: string | null;
  plan_type?: string | null;
  current_period_end?: number | null;
};

const normalizeStatus = (value?: string | null) => (value || 'unknown').toLowerCase();

const isActiveStatus = (value: string) => value === 'active' || value === 'trialing';

const isCancelledStatus = (value: string) =>
  value === 'canceled' || value === 'cancelled' || value === 'incomplete_expired';

const tierRank: Record<string, number> = {
  tier1: 1,
  tier2: 2,
  tier3: 3,
};

export async function trackSubscriptionStatusChange(status?: {
  subscription_status?: string | null;
  plan_tier?: string | null;
  plan_type?: string | null;
  current_period_end?: number | null;
}) {
  if (!posthog || !status) return;
  try {
    const snapshot: BillingSnapshot = {
      status: normalizeStatus(status.subscription_status),
      plan_tier: status.plan_tier ?? null,
      plan_type: status.plan_type ?? null,
      current_period_end: status.current_period_end ?? null,
    };

    let previous: BillingSnapshot | null = null;
    try {
      const raw = await AsyncStorage.getItem(BILLING_SNAPSHOT_KEY);
      if (raw) {
        previous = JSON.parse(raw) as BillingSnapshot;
      }
    } catch {
      previous = null;
    }

    if (previous) {
      if (!isActiveStatus(previous.status) && isActiveStatus(snapshot.status)) {
        captureEvent('subscription_started', {
          from_status: previous.status,
          to_status: snapshot.status,
          plan_tier: snapshot.plan_tier,
          plan_type: snapshot.plan_type,
        });
      }

      if (isActiveStatus(previous.status) && isCancelledStatus(snapshot.status)) {
        captureEvent('subscription_cancelled', {
          from_status: previous.status,
          to_status: snapshot.status,
          plan_tier: snapshot.plan_tier,
          plan_type: snapshot.plan_type,
        });
      }

      if (
        previous.plan_tier &&
        snapshot.plan_tier &&
        previous.plan_tier !== snapshot.plan_tier
      ) {
        const prevRank = tierRank[previous.plan_tier];
        const nextRank = tierRank[snapshot.plan_tier];
        const changeProps = {
          from_plan_tier: previous.plan_tier,
          to_plan_tier: snapshot.plan_tier,
          plan_type: snapshot.plan_type,
        };
        if (prevRank && nextRank) {
          if (nextRank > prevRank) {
            captureEvent('subscription_upgraded', changeProps);
          } else if (nextRank < prevRank) {
            captureEvent('subscription_downgraded', changeProps);
          } else {
            captureEvent('subscription_plan_changed', changeProps);
          }
        } else {
          captureEvent('subscription_plan_changed', changeProps);
        }
      }

      if (
        isActiveStatus(previous.status) &&
        isActiveStatus(snapshot.status) &&
        typeof previous.current_period_end === 'number' &&
        typeof snapshot.current_period_end === 'number' &&
        snapshot.current_period_end > previous.current_period_end
      ) {
        captureEvent('subscription_renewed', {
          plan_tier: snapshot.plan_tier,
          plan_type: snapshot.plan_type,
          from_period_end: previous.current_period_end,
          to_period_end: snapshot.current_period_end,
        });
      }
    }

    await AsyncStorage.setItem(BILLING_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // no-op
  }
}

/**
 * Paywall-abandonment tracking for the two-step "comeback" → "final offer"
 * discount ladder.
 *
 * Client-side signal only — a heuristic used to decide whether to *show*
 * an offer. The backend independently re-validates real eligibility
 * (account age, subscription history, one-time use) on every checkout
 * request, so this state can be stale or wrong without causing incorrect
 * charges — see getPaywallOfferDecline() in
 * services/api.ts for how a wrong guess here is handled gracefully.
 */
export type PaywallOfferLadderState = {
  /** ISO timestamp of the first time the user ever saw plan selection. */
  firstPaywallViewAt: string | null;
  /** Whether the user has ever started a checkout (any plan, any surface). */
  checkoutEverInitiated: boolean;
  /** ISO timestamp of the first "decline moment" — left the paywall without
   *  ever starting checkout (in-app navigation away, or dismissed a modal on
   *  this screen). Once set, the comeback offer shows on the *next* arrival
   *  instead of waiting out the 24h window. */
  leftWithoutCheckoutAt: string | null;
  /** The comeback offer (step 1) has been rendered at least once. Gates step 2. */
  comebackShown: boolean;
  /** User explicitly closed the comeback offer — don't re-show step 1. */
  comebackDismissed: boolean;
  /** Backend declined/consumed the comeback offer once — don't re-show step 1. */
  comebackDeclined: boolean;
  /** User explicitly closed the final offer — floor: nothing shows again. */
  finalDismissed: boolean;
  /** Backend declined/consumed the final offer once — floor: nothing shows again. */
  finalDeclined: boolean;
};

const COMEBACK_OFFER_MIN_ABANDONMENT_MS = 24 * 60 * 60 * 1000;

/**
 * Records the first time the user lands on paywall / plan-selection. Only
 * the first occurrence is persisted (idempotent) so it anchors the
 * abandonment window instead of resetting on every visit.
 */
export async function recordPaywallView(): Promise<void> {
  try {
    const existing = await AsyncStorage.getItem(PAYWALL_FIRST_VIEW_KEY);
    if (!existing) {
      await AsyncStorage.setItem(PAYWALL_FIRST_VIEW_KEY, new Date().toISOString());
    }
  } catch {
    // no-op — never let analytics block product behaviour
  }
}

/** Records that the user has started a checkout at least once (any plan). */
export async function recordCheckoutInitiated(): Promise<void> {
  try {
    await AsyncStorage.setItem(PAYWALL_CHECKOUT_INITIATED_KEY, '1');
  } catch {
    // no-op
  }
}

/**
 * Records a "decline moment" — the user left plan selection (in-app
 * navigation away, or dismissed a modal on this screen) without ever
 * starting a checkout during that visit. Idempotent: only the first
 * occurrence is kept, since presence (not recency) is what matters — it
 * makes the comeback offer show immediately on the next arrival instead of
 * waiting for the 24h window.
 */
export async function recordPaywallLeftWithoutCheckout(): Promise<void> {
  try {
    const existing = await AsyncStorage.getItem(PAYWALL_LEFT_WITHOUT_CHECKOUT_KEY);
    if (!existing) {
      await AsyncStorage.setItem(PAYWALL_LEFT_WITHOUT_CHECKOUT_KEY, new Date().toISOString());
    }
  } catch {
    // no-op
  }
}

/** Records that the comeback offer (step 1) has been shown at least once. */
export async function recordComebackOfferShown(): Promise<void> {
  try {
    await AsyncStorage.setItem(COMEBACK_OFFER_SHOWN_KEY, '1');
  } catch {
    // no-op
  }
}

/** User dismissed the comeback offer banner — never show it again. */
export async function recordComebackOfferDismissed(): Promise<void> {
  try {
    await AsyncStorage.setItem(COMEBACK_OFFER_DISMISSED_KEY, '1');
  } catch {
    // no-op
  }
}

/** Backend declined/consumed the comeback offer once — never show it again. */
export async function recordComebackOfferDeclined(): Promise<void> {
  try {
    await AsyncStorage.setItem(COMEBACK_OFFER_DECLINED_KEY, '1');
  } catch {
    // no-op
  }
}

/** User explicitly closed the final offer — floor of the ladder. */
export async function recordFinalOfferDismissed(): Promise<void> {
  try {
    await AsyncStorage.setItem(FINAL_OFFER_DISMISSED_KEY, '1');
  } catch {
    // no-op
  }
}

/** Backend declined/consumed the final offer once — floor of the ladder. */
export async function recordFinalOfferDeclined(): Promise<void> {
  try {
    await AsyncStorage.setItem(FINAL_OFFER_DECLINED_KEY, '1');
  } catch {
    // no-op
  }
}

export async function getPaywallOfferLadderState(): Promise<PaywallOfferLadderState> {
  try {
    const [
      firstPaywallViewAt,
      checkoutInitiatedRaw,
      leftWithoutCheckoutAt,
      comebackShownRaw,
      comebackDismissedRaw,
      comebackDeclinedRaw,
      finalDismissedRaw,
      finalDeclinedRaw,
    ] = await Promise.all([
      AsyncStorage.getItem(PAYWALL_FIRST_VIEW_KEY),
      AsyncStorage.getItem(PAYWALL_CHECKOUT_INITIATED_KEY),
      AsyncStorage.getItem(PAYWALL_LEFT_WITHOUT_CHECKOUT_KEY),
      AsyncStorage.getItem(COMEBACK_OFFER_SHOWN_KEY),
      AsyncStorage.getItem(COMEBACK_OFFER_DISMISSED_KEY),
      AsyncStorage.getItem(COMEBACK_OFFER_DECLINED_KEY),
      AsyncStorage.getItem(FINAL_OFFER_DISMISSED_KEY),
      AsyncStorage.getItem(FINAL_OFFER_DECLINED_KEY),
    ]);
    return {
      firstPaywallViewAt,
      checkoutEverInitiated: checkoutInitiatedRaw === '1',
      leftWithoutCheckoutAt,
      comebackShown: comebackShownRaw === '1',
      comebackDismissed: comebackDismissedRaw === '1',
      comebackDeclined: comebackDeclinedRaw === '1',
      finalDismissed: finalDismissedRaw === '1',
      finalDeclined: finalDeclinedRaw === '1',
    };
  } catch {
    return {
      firstPaywallViewAt: null,
      checkoutEverInitiated: false,
      leftWithoutCheckoutAt: null,
      comebackShown: false,
      comebackDismissed: false,
      comebackDeclined: false,
      finalDismissed: false,
      finalDeclined: false,
    };
  }
}

/**
 * Step 1 — comeback offer (30% off). Triggers on either signal:
 *   (a) a prior "decline moment" was recorded (immediate on next arrival), or
 *   (b) the first-ever paywall view is >=24h old.
 * Never re-shown once dismissed/declined, and never shown at all once the
 * ladder has reached its floor (final offer dismissed/declined). Callers
 * must additionally check subscription status — this module doesn't own
 * billing state.
 */
export function isComebackOfferEligibleClientSide(state: PaywallOfferLadderState): boolean {
  if (state.checkoutEverInitiated) return false;
  if (state.comebackDismissed || state.comebackDeclined) return false;
  if (state.finalDismissed || state.finalDeclined) return false;
  if (state.leftWithoutCheckoutAt) return true;
  if (!state.firstPaywallViewAt) return false;
  const firstView = new Date(state.firstPaywallViewAt).getTime();
  if (Number.isNaN(firstView)) return false;
  return Date.now() - firstView >= COMEBACK_OFFER_MIN_ABANDONMENT_MS;
}

/**
 * Step 2 — final offer (fixed first-month price, paid, no trial). Strictly
 * escalating: only eligible once the comeback offer has actually been shown
 * AND the user dismissed or was declined it. This is the floor of the
 * ladder — once dismissed/declined itself, nothing shows again, ever.
 */
export function isFinalOfferEligibleClientSide(state: PaywallOfferLadderState): boolean {
  if (!state.comebackShown) return false;
  if (!(state.comebackDismissed || state.comebackDeclined)) return false;
  if (state.finalDismissed || state.finalDeclined) return false;
  return true;
}
